/* eslint-disable react-hooks/exhaustive-deps */
import {
  Box,
  Button,
  Typography,
  Dialog,
  DialogTitle,
  Divider,
  DialogContent,
  CircularProgress,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import CloudSyncOutlinedIcon from "@mui/icons-material/CloudSyncOutlined";
import StorageOutlinedIcon from "@mui/icons-material/StorageOutlined";
import FolderCopyOutlinedIcon from "@mui/icons-material/FolderCopyOutlined";
import DynamicDataGrid from "../../components/Composables/DynamicDataGrid";
import API from "../../Utils/Interceptor/ApiMiddleware";
import CustomSnackbar from "../../components/Composables/CustomSnackBarMenu";

export default function Backup() {
  const [backupData, setBackupData] = useState([]);
  const [loadingData, setLoadingData] = useState(true);
  const [loadingPage, setLoadingPage] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [backupTypeToRun, setBackupTypeToRun] = useState(null);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "error",
  });
  const handleSnackbarClose = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };
  const [popupOpen, setPopupOpen] = useState(false);

  const formatSize = (mb) => {
    if (mb < 1024) return `${mb.toFixed(2)} MB`;
    return `${(mb / 1024).toFixed(2)} GB`;
  };

  const formatDateTimeGMT = (dateStr) => {
    const gmt = new Date(dateStr + " GMT");
    return gmt.toUTCString();
  };

  const getBackupData = async () => {
    setLoadingData(true);
    try {
      const res = await API.get("/get-backups");
      if (res.status === 200 && res.data?.data) {
        const formattedData = res.data.data.map((item) => ({
          id: item.fileName,
          "File Name": item.fileName,
          Size: formatSize(item.sizeMB),
          Date: formatDateTimeGMT(item.lastModified),
          url: item.url,
        }));
        setBackupData(formattedData);
      } else {
        setBackupData([]);
      }
    } catch (error) {
      console.error("API error:", error);
      setBackupData([]);
    } finally {
      setLoadingData(false);
    }
  };

  const databaseBackup = async () => {
    setLoadingPage(true);
    try {
      const res = await API.get("/create-database-backup");
      if (res.data?.success && res.data?.message === 200) {
        setPopupOpen(true);
      } else {
        setSnackbar({
          open: true,
          message: "Failed to start database backup",
          severity: "error",
        });
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: "Error creating database backup",
        severity: "error",
      });
    } finally {
      setLoadingPage(false);
    }
  };

  const fileBackup = async () => {
    setLoadingPage(true);
    try {
      const res = await API.get("/create-attachments-backup");
      if (res.data?.success && res.data?.message === 200) {
        setPopupOpen(true);
      } else {
        setSnackbar({
          open: true,
          message: "Attachments backup failed",
          severity: "error",
        });
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: "Error creating attachments backup",
        severity: "error",
      });
    } finally {
      setLoadingPage(false);
    }
  };

  const fullBackup = async () => {
    setLoadingPage(true);
    try {
      const [res1, res2] = await Promise.all([
        API.get("/create-database-backup"),
        API.get("/create-attachments-backup"),
      ]);

      if (res1.data?.message === 200 && res2.data?.message === 200) {
        setPopupOpen(true);
      } else {
        setSnackbar({
          open: true,
          message: "Full backup failed",
          severity: "error",
        });
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: "Error during full backup",
        severity: "error",
      });
    } finally {
      setLoadingPage(false);
    }
  };

  const handleConfirmBackup = () => {
    setConfirmDialogOpen(false);
    if (backupTypeToRun === "database") databaseBackup();
    else if (backupTypeToRun === "file") fileBackup();
    else if (backupTypeToRun === "full") fullBackup();
    setBackupTypeToRun(null);
  };
  const handleCancelBackup = () => {
    setConfirmDialogOpen(false);
    setBackupTypeToRun(null);
  };

  useEffect(() => {
    getBackupData();
    console.log("backupData", backupData);
  }, []);

  return (
    <Box
      sx={{
        height: "100%",
        padding: "16px",
        backgroundColor: "#E3F2FD",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        borderRadius: "24px 0 0 0",
      }}
    >
      {loadingPage && (
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            zIndex: 10,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <CircularProgress />
        </Box>
      )}

      <Box
        sx={{
          height: "200px",
          backgroundColor: "white",
          padding: "0px 72px",
          borderRadius: "12px",
          display: "flex",
          alignItems: "center",
          gap: "32px",
          flexShrink: 0,
        }}
      >
        <Box sx={{ height: "200px" }}>
          <img
            src={`${process.env.REACT_APP_BASEURL}/logo/Img_Tile.svg`}
            alt="Backup Illustration"
            style={{
              height: "100%",
              objectFit: "cover",
            }}
          />
        </Box>

        <Box
          sx={{
            gap: "12px",
            display: "flex",
            flexDirection: "column",
            padding: "0px 64px",
          }}
        >
          <Box sx={{ gap: "8px", display: "flex", flexDirection: "column" }}>
            <Typography
              variant="h6"
              sx={{ fontSize: "20px", fontWeight: "600" }}
            >
              Backup
            </Typography>
            <Typography color="#242437CC" fontSize="16px">
              The Gallery currently holds a significant amount of data, which
              can take considerable time to load. For a faster experience :
            </Typography>
          </Box>
          <Box sx={{ display: "flex", gap: "16px" }}>
            <Button
              variant="contained"
              color="primary"
              size="small"
              startIcon={<CloudSyncOutlinedIcon />}
              onClick={() => {
                setBackupTypeToRun("full");
                setConfirmDialogOpen(true);
              }}
            >
              Full Backup
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<StorageOutlinedIcon />}
              onClick={() => {
                setBackupTypeToRun("database");
                setConfirmDialogOpen(true);
              }}
            >
              Database Backup
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<FolderCopyOutlinedIcon />}
              onClick={() => {
                setBackupTypeToRun("file");
                setConfirmDialogOpen(true);
              }}
            >
              Attachments Backup
            </Button>
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          borderRadius: "12px",
          padding: "16px",
          backgroundColor: "white",
          flexGrow: 1,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          mt: 2,
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontSize: "18px",
            fontWeight: "600",
            marginBottom: "16px",
          }}
        >
          Recent 10 Backups
        </Typography>
        <Box sx={{ flexGrow: 1, overflow: "hidden" }}>
          <DynamicDataGrid
            rows={backupData}
            loading={loadingData}
            hideFooter={true}
          />
        </Box>
      </Box>

      <CustomSnackbar
        open={snackbar.open}
        onClose={handleSnackbarClose}
        message={snackbar.message}
        severity={snackbar.severity}
      />
      <Dialog
        open={popupOpen}
        onClose={() => setPopupOpen(false)}
        PaperProps={{
          sx: {
            width: "300px",
            borderRadius: "12px",
            textAlign: "center",
          },
        }}
      >
        <DialogTitle
          sx={{
            fontSize: "18px",
            fontWeight: "bold",
            padding: "8px 16px",
            textAlign: "start",
          }}
        >
          Backup in Progress
        </DialogTitle>
        <Divider />

        <DialogContent>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 2,
            }}
          >
            <img
              src={`${process.env.REACT_APP_BASEURL}/logo/PopUp_img.svg`}
              alt="Backup in progress"
              style={{
                width: "100%",
                height: "auto",
                maxHeight: "160px",
                objectFit: "contain",
              }}
            />
            <Typography fontSize="14px" color="text.secondary">
              Your backup has started. You'll receive an email notification once
              the process is complete.
            </Typography>
            <Button
              variant="contained"
              onClick={() => setPopupOpen(false)}
              sx={{
                width: "100%",
                borderRadius: "8px",
                textTransform: "none",
              }}
            >
              OK
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      <Dialog
        open={confirmDialogOpen}
        onClose={handleCancelBackup}
        PaperProps={{
          sx: {
            width: "400px",
            borderRadius: "12px",
            textAlign: "center",
          },
        }}
      >
        <DialogTitle
          sx={{
            fontSize: "18px",
            fontWeight: "bold",
            padding: "8px 16px",
            textAlign: "start",
          }}
        >
          Confirm Backup
        </DialogTitle>
        <Divider />
        <DialogContent>
          <Typography sx={{ marginBottom: "16px", fontSize: "14px" }}>
            Are you sure you want to start the{" "}
            <strong>{backupTypeToRun?.toUpperCase()}</strong> backup?
          </Typography>
          <Box
            sx={{ display: "flex", justifyContent: "space-between", gap: 1 }}
          >
            <Button
              variant="outlined"
              onClick={handleCancelBackup}
              sx={{ flex: 1 }}
            >
              No
            </Button>
            <Button
              variant="contained"
              onClick={handleConfirmBackup}
              sx={{ flex: 1 }}
            >
              Yes
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
}
